import React, { useMemo, useState } from 'react';
import { toast } from '../../toasts';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import AssetService from '../../services/asset.service';
import { getError, showCurrency } from '../../utils';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, Pill, RowActionsMenu, Button } from '../../components/EntityList';
import AssetEditor from '../Assets/AssetEditor';
import { useLabels } from '../../hooks/useLabels';

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function AccountAssets({ account, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();

	// Sends the editor back to this exact tab on save/cancel instead of letting
	// it land on the newly created asset's detail page.
	const returnTo = () => encodeURIComponent(location.pathname + location.search);
	const [showEditorModal, setShowEditorModal] = useState(false);
	const [editingAsset, setEditingAsset] = useState(null);
	const [deletingAsset, setDeletingAsset] = useState(null);

	const closeEditorModal = () => {
		setShowEditorModal(false);
		setEditingAsset(null);
	};

	const stats = useMemo(() => {
		const enUso = rows.filter((asset) => !asset.sold);
		const valorByCurrency = new Map();
		enUso.forEach((asset) => {
			const key = asset.currency || '—';
			valorByCurrency.set(key, (valorByCurrency.get(key) || 0) + (asset.price || 0));
		});
		return [
			{ label: 'En uso', value: enUso.length },
			{
				label: 'Valor total',
				value: [...valorByCurrency.entries()].map(([currency, price]) => formatMoney(currency, price)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows]);

	async function handleConfirmDelete() {
		try {
			await AssetService.delete(deletingAsset._id);
			toast.success('Activo Eliminado');
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingAsset(null);
		}
	}

	const columns = [
		{ key: 'id', header: 'ID', render: (asset) => <IdChip>{asset._id?.slice(-6)}</IdChip> },
		{ key: 'name', header: 'Nombre', render: (asset) => asset.name },
		{ key: 'type', header: 'Categoría', render: (asset) => <Pill tone='neutral'>{asset.type}</Pill> },
		{
			key: 'price',
			header: 'Valor',
			className: 'tw:text-right',
			render: (asset) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(asset.currency, asset.price)}</span>,
		},
		{
			key: 'estado',
			header: 'Estado',
			render: (asset) => <Pill tone={asset.sold ? 'neutral' : 'ok'}>{asset.sold ? 'Vendido' : 'En uso'}</Pill>,
		},
	];

	const renderRowActions = (asset) => (
		<RowActionsMenu
			items={[
				{ label: 'Editar', onClick: () => { setEditingAsset(asset); setShowEditorModal(true); } },
				{ label: 'Eliminar', onClick: () => setDeletingAsset(asset), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end'>
				<Button size='sm' variant='primary' onClick={() => navigate(`/AdminScreen/accounts/account/${account._id}/newAsset?returnTo=${returnTo()}`)}>
					Nuevo activo
				</Button>
			</div>
			<Section title='Activos'>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={(asset) => navigate(`/AdminScreen/assets/asset/${asset._id}`)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron activos para esta cuenta.'
				/>
			</Section>

			<Modal show={showEditorModal} onHide={closeEditorModal} size='lg' backdrop='static' centered>
				<Modal.Header closeButton>
					<Modal.Title>{editingAsset ? 'Editar Activo' : 'Nuevo Activo'}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<AssetEditor
						mode={editingAsset ? 'edit' : 'create'}
						initialData={editingAsset || null}
						onCancel={closeEditorModal}
						onSaved={() => {
							onRefetch();
							closeEditorModal();
						}}
						disableOwner={true}
						preloadOwner={account}
						saveLabel={editingAsset ? 'Guardar' : 'Agregar'}
					/>
				</Modal.Body>
			</Modal>

			<ConfirmationModal
				show={Boolean(deletingAsset)}
				handleClose={() => setDeletingAsset(null)}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteAsset')}
				description={t('msg.confirmDeleteAsset')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default AccountAssets;
